/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export const VALIDATION_METHOD_TYPES = ['email', 'phone', 'questions'] as const;

export type ValidationMethodType = (typeof VALIDATION_METHOD_TYPES)[number];

/** Identity validation method of a PENDING shopper; `value` is the masked email or phone from Auth Extension. */
export type ValidationMethod = { type: ValidationMethodType; value?: string };

/** Masked values are short; anything longer didn't come from Auth Extension. */
const MAX_VALUE_LENGTH = 100;

export function isValidationMethodType(value: unknown): value is ValidationMethodType {
    return typeof value === 'string' && (VALIDATION_METHOD_TYPES as readonly string[]).includes(value);
}

/**
 * Reads the validation methods the method-selection form posts back (JSON), keeping only well-formed
 * entries. They are only displayed again; Commerce Cloud validates the chosen method on its side.
 */
export function parseValidationMethods(json: string | undefined): ValidationMethod[] {
    if (!json) {
        return [];
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return [];
    }

    if (!Array.isArray(parsed)) {
        return [];
    }

    return parsed.flatMap((entry: unknown) => {
        if (!entry || typeof entry !== 'object' || !isValidationMethodType((entry as ValidationMethod).type)) {
            return [];
        }
        const { type, value } = entry as ValidationMethod;
        return typeof value === 'string' && value.length <= MAX_VALUE_LENGTH ? [{ type, value }] : [{ type }];
    });
}
